import { toDayjs } from './myndighetsdatum';

/**
 * Kopplar ihop verklig bokföringsdata med regelmotorns uppgifts-id.
 *
 * Nyckelformen måste vara identisk med den `myndighetsdatum.ts` bygger
 * (`agd-YYYY-MM`, `moms-YYYY-MM`) — glider de isär slutar avbockningen fungera
 * utan att något syns. Rena funktioner utan HTTP-beroenden så att kopplingen
 * kan testas.
 */

/** `prefix-YYYY-MM` utifrån periodens första dag. */
export function periodnyckel(prefix: string, förstaDagIPerioden: unknown): string | null {
  const dag = toDayjs(förstaDagIPerioden);
  return dag ? `${prefix}-${dag.format('YYYY-MM')}` : null;
}

/**
 * Arbetsgivardeklarationer som är bokförda. `bokförd` sätts av backend när
 * kvittensen laddats upp och affärshändelsen slutförts.
 */
export function bokfördaArbetsgivardeklarationer(lista: unknown): Record<string, string> {
  if (!Array.isArray(lista)) return {};
  return lista.reduce<Record<string, string>>((acc, agd: any) => {
    if (!agd?.bokförd) return acc;
    const nyckel = periodnyckel('agd', agd?.period?.förstaDag);
    if (nyckel) acc[nyckel] = 'Bokförd';
    return acc;
  }, {});
}

/**
 * Momsdeklarationer som är inlämnade. Momsdomänen saknar `bokförd`-flagga, så
 * `inlämnadPå` är signalen — vilket också är precis vad uppgiften efterfrågar.
 */
export function inlämnadeMomsdeklarationer(lista: unknown): Record<string, string> {
  if (!Array.isArray(lista)) return {};
  return lista.reduce<Record<string, string>>((acc, deklaration: any) => {
    if (!deklaration?.inlämnadPå) return acc;
    const periodStart = toDayjs(deklaration?.period?.förstaDag ?? deklaration?.periodStart);
    const periodSlut = toDayjs(deklaration?.period?.sistaDag ?? deklaration?.periodSlut);
    
    // Månads- eller kvartalsmoms
    if (periodStart) {
      const nyckel = `${periodStart.format('YYYY-MM')}`;
      acc[`moms-${nyckel}`] = 'Inlämnad';
    }
    // Årsmoms (helår)
    if (periodSlut) {
      acc[`moms-ar-${periodSlut.format('YYYY-MM-DD')}`] = 'Inlämnad';
    }
    return acc;
  }, {});
}

/**
 * Inkomstdeklarationer som är inlämnade. Nyckel matchar `ink-YYYY-MM-DD`.
 */
export function inlämnadeInkomstdeklarationer(lista: unknown): Record<string, string> {
  if (!Array.isArray(lista)) return {};
  return lista.reduce<Record<string, string>>((acc, ink: any) => {
    const sistaDag = toDayjs(ink?.raTom ?? ink?.räkenskapsårSistaDag ?? ink?.sistaDag);
    if (sistaDag) {
      const statusText = ink?.status ?? '';
      const ärInlämnad =
        !statusText ||
        statusText.toLowerCase().includes('inlämnad') ||
        statusText.toLowerCase().includes('fastställd') ||
        statusText.toLowerCase().includes('mottagen');

      if (ärInlämnad) {
        const datumStr = sistaDag.format('YYYY-MM-DD');
        acc[`ink-${datumStr}`] = 'Inlämnad';
        acc[`moms-ar-${datumStr}`] = 'Inlämnad';
      }
    }
    return acc;
  }, {});
}

/**
 * Föreningsstämmor/årsstämmor som är genomförda och protokollförda.
 * Nyckel matchar `stamma-YYYY-MM-DD`.
 */
export function genomfördaStämmor(lista: unknown): Record<string, string> {
  if (!Array.isArray(lista)) return {};
  return lista.reduce<Record<string, string>>((acc, mote: any) => {
    const titel = (mote?.styrelseprotokoll?.titel || mote?.titel || '').toLowerCase();
    const ärStämma = titel.includes('stämma') || titel.includes('stamm');
    const status = mote?.status ?? '';
    const protokollStatus = mote?.styrelseprotokoll?.status ?? '';
    const ärGenomförd =
      status === 'AVSLUTAT' ||
      status === 'GENOMFORT' ||
      protokollStatus === 'SIGNERAT' ||
      protokollStatus === 'JUSTERAT' ||
      Boolean(mote?.genomfördVid);

    if (ärStämma && ärGenomförd) {
      const datum = toDayjs(mote?.genomfördVid ?? mote?.föreslagenTidpunkt);
      // Om stämman hölls 2026 avser den räkenskapsåret 2025-12-31
      if (titel.includes('2025') || (datum && datum.year() === 2026)) {
        acc['stamma-2025-12-31'] = 'Genomförd';
      }
      if (titel.includes('2024') || (datum && datum.year() === 2025)) {
        acc['stamma-2024-12-31'] = 'Genomförd';
      }
    }
    return acc;
  }, {});
}
